import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: 'Home', path: '/homepage-hero-experience-hub', icon: 'Home' },
    { name: 'About', path: '/about-professional-story-center', icon: 'User' },
    { name: 'Projects', path: '/projects-innovation-laboratory', icon: 'Code' },
    { name: 'Experience', path: '/experience-career-journey-timeline', icon: 'Briefcase' },
    { name: 'Skills', path: '/skills-technical-mastery-showcase', icon: 'Zap' },
    { name: 'Contact', path: '/contact-collaboration-gateway', icon: 'Mail' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };
  
  const handleDownloadCV = () => {
    const link = document.createElement('a');
    link.href = '/assets/Lahiru Senavirathna.pdf';
    link.download = 'Lahiru Senavirathna.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const headerVariants = {
    top: {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(8px)',
      boxShadow: 'none',
      transition: { duration: 0.3 }
    },
    scrolled: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.header 
      variants={headerVariants}
      animate={isScrolled ? 'scrolled' : 'top'}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/50"
    >
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <Link 
            to="/homepage-hero-experience-hub" 
            className="flex items-center space-x-3 hover:opacity-80 transition-premium"
            onClick={closeMenu}
          >
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shadow-premium overflow-hidden">
                <img 
                  src="/assets/images/gmail.png" 
                  alt="CodeShow LapZ Logo" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-success rounded-full border-2 border-white animate-pulse"></div>
            </motion.div>
            <div className="hidden sm:block">
                              <h1 className="text-xl font-bold gradient-text">CodeShow LapZ</h1>
              <p className="text-xs text-text-secondary -mt-1">Full-Stack Engineer</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item, index) => (
              <motion.div
                key={item.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-premium hover:bg-surface-hover hover-scale ${
                    isActivePath(item.path)
                      ? 'bg-primary/10 text-primary border border-primary/20' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name={item.icon} size={16} />
                  <span>{item.name}</span>
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Desktop Theme Toggle & Download CV */}
          <div className="hidden lg:flex items-center space-x-3">
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="text-sm hover-lift"
              onClick={handleDownloadCV}
            >
              Download CV
            </Button>
          </div>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="flex items-center space-x-2 lg:hidden">
            <ThemeToggle size="sm" />
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="text-sm hover-lift mr-2"
              onClick={handleDownloadCV}
            >
              CV
            </Button>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={toggleMenu}
              className="p-2 rounded-lg hover:bg-surface-hover transition-premium"
              aria-label="Toggle menu"
            >
              <motion.div
                animate={isMenuOpen ? { rotate: 90 } : { rotate: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Icon 
                  name={isMenuOpen ? 'X' : 'Menu'} 
                  size={24} 
                  className="text-text-primary"
                />
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <motion.div 
          initial={false}
          animate={isMenuOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="lg:hidden overflow-hidden"
        >
          <div className="px-6 py-4 bg-background/95 backdrop-blur-md border-t border-border">
            <nav className="space-y-2">
              {navigationItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isMenuOpen ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.path}
                    onClick={closeMenu}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-premium hover-lift ${
                      isActivePath(item.path)
                        ? 'bg-primary/10 text-primary border border-primary/20' : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
                    }`}
                  >
                    <Icon name={item.icon} size={20} />
                    <span>{item.name}</span>
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Mobile CTA */}
            <motion.div 
              className="mt-6 pt-4 border-t border-border"
              initial={{ opacity: 0, y: 10 }}
              animate={isMenuOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ delay: 0.2 }}
            >
              <Button
                variant="outline"
                fullWidth
                iconName="Download"
                iconPosition="left"
                className="hover-lift"
                onClick={handleDownloadCV}
              >
                Download CV
              </Button>
            </motion.div>

            {/* Availability Status */}
            <motion.div 
              className="mt-4 p-3 bg-success/10 rounded-lg border border-success/20"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isMenuOpen ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-success">Available for new projects</span>
              </div>
              <p className="text-xs text-text-muted mt-1">Next availability: January 2025</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header;